function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
import { css } from '@emotion/react';
import _ from 'lodash';
import { useCallback, useLayoutEffect } from 'react';
import { useForwardingRef, useRefMap } from '@arc/frontend/util-common';
const sides = ['top', 'bottom', 'left', 'right'];
const styles = {
  root: process.env.NODE_ENV === "production" ? {
    name: "bjn8wh",
    styles: "position:relative"
  } : {
    name: "1e99pvw-root",
    styles: "position:relative;label:root;",
    toString: _EMOTION_STRINGIFIED_CSS_ERROR__
  },
  scrollArea: process.env.NODE_ENV === "production" ? {
    name: "171og9i",
    styles: "width:100%;height:100%;overflow:auto;isolation:isolate"
  } : {
    name: "z92x9o-scrollArea",
    styles: "width:100%;height:100%;overflow:auto;isolation:isolate;label:scrollArea;",
    toString: _EMOTION_STRINGIFIED_CSS_ERROR__
  },
  shadowOverlay: side => /*#__PURE__*/css({
    pointerEvents: 'none',
    backgroundImage: `linear-gradient(to ${side}, #00000000, #00000020)`,
    position: 'absolute',
    // Set opacity to 0 initially so that the shadows are not visible before they are positioned.
    opacity: 0
  }, process.env.NODE_ENV === "production" ? "" : ";label:shadowOverlay;")
};
/**
 * A transparent box that shows shadows on the sides where its content overflows.
 */
export function ScrollShadowBox(props) {
  const {
    children,
    scrollAreaStyle,
    height,
    outerStyle,
    scrollAreaRef: propsScrollAreaRef,
    scrollAreaTestid,
    topOffset,
    bottomOffset,
    leftOffset,
    rightOffset,
    sidesDisabled = [],
    ...rest
  } = props;
  const scrollAreaRef = useForwardingRef(null, [propsScrollAreaRef]);
  const shadowRefs = useRefMap();
  const updateScrollShadows = useCallback(() => {
    if (!scrollAreaRef.current) return;
    const element = scrollAreaRef.current;
    const offset = {
      top: topOffset ?? 0,
      bottom: (bottomOffset ?? 0) + (element.offsetHeight - element.clientHeight),
      left: leftOffset ?? 0,
      right: (rightOffset ?? 0) + element.offsetWidth - element.clientWidth
    };
    const scrollDistance = {
      top: element.scrollTop,
      bottom: element.scrollHeight - element.clientHeight - element.scrollTop,
      left: element.scrollLeft,
      right: element.scrollWidth - element.clientWidth - element.scrollLeft
    };

    // Because this could be called 60 times a second, use imperative updates
    // instead of useState to avoid potential performance issues from
    // re-rendering the component and re-creating Emotion styles.
    for (const side of sides) {
      const style = {
        opacity: _.clamp(scrollDistance[side] / 30, 0, 1).toString(),
        ...(side === 'top' || side === 'bottom' ? {
          height: '0.5rem',
          left: `${offset.left}px`,
          right: `${offset.right}px`,
          [side]: `${offset[side]}px`
        } : {
          width: '0.5rem',
          top: `${offset.top}px`,
          bottom: `${offset.bottom}px`,
          [side]: `${offset[side]}px`
        })
      };
      const refToUpdate = shadowRefs(side).current;
      if (refToUpdate) {
        Object.assign(refToUpdate.style, sidesDisabled.includes(side) ? null : style);
      }
    }
  }, [bottomOffset, leftOffset, rightOffset, scrollAreaRef, shadowRefs, sidesDisabled, topOffset]);
  useLayoutEffect(() => {
    // Set shadows on initial render and offset changes.
    updateScrollShadows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bottomOffset, leftOffset, rightOffset, topOffset]);
  return <div css={[styles.root, outerStyle, process.env.NODE_ENV === "production" ? "" : ";label:ScrollShadowBox;"]} style={{
    height
  }}>
			<div {...rest} ref={scrollAreaRef} onScroll={updateScrollShadows} css={[styles.scrollArea, scrollAreaStyle, process.env.NODE_ENV === "production" ? "" : ";label:ScrollShadowBox;"]} data-scrollarea data-testid={scrollAreaTestid} id={scrollAreaTestid}>
				{children}
			</div>

			{sides.map(side => <div key={side} ref={shadowRefs(side)} css={styles.shadowOverlay(side)} />)}
		</div>;
}